/**
 * SearchUserSelectedUsersHeader Component
 * Horizontal scrollable list of selected users with remove buttons
 * Matches Flutter implementation: lib/presentation/bottom_sheet/search_users_bottom_sheet.dart - SelectedUsersPersistentHeader
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { UserUtils } from '@/domain/utils/userUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';

export interface SearchUserSelectedUsersHeaderProps {
  subscriptions: ExclusiveContentSubscriptionEntity[];
  onRemove: (subscription: ExclusiveContentSubscriptionEntity) => void;
  emptyMessage?: string;
}

export const SearchUserSelectedUsersHeader: React.FC<SearchUserSelectedUsersHeaderProps> = ({
  subscriptions,
  onRemove,
  emptyMessage,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Min height based on avatar size + padding (top and bottom) + borderBottom
  const minHeight = responsive.avatar.small + responsive.spacing.lg * 2 + responsive.border.thin;

  if (subscriptions.length === 0 && emptyMessage) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          minHeight,
          padding: responsive.spacing.lg,
          borderBottom: `${responsive.border.thin}px solid ${theme.border}`,
          color: theme.text.secondary,
        }}
      >
        <p style={{ margin: 0 }}>{emptyMessage}</p>
      </div>
    );
  }

  return (
    // `scrollbar-hide` hides the scrollbar without disabling scrolling: Flutter paints none here
    // (bare horizontal `ListView.builder`, no `Scrollbar` wrapper —
    // search_users_bottom_sheet.dart:951-960), and the sibling sheets do the same
    // (ContentUsersLikedContent.tsx:114-122). Keep `overflowX: 'auto'` — this strip must still
    // scroll sideways when many users are selected.
    <div
      className="scrollbar-hide"
      {...qaAttr({ id: 'search-users-selected-scroll' })}
      style={{
        display: 'flex',
        gap: responsive.spacing.sm,
        padding: responsive.spacing.lg,
        minHeight,
        overflowX: 'auto',
        borderBottom: `${responsive.border.thin}px solid ${theme.border}`,
      }}
    >
      {subscriptions.map((sub) => (
        <div
          key={sub.userId}
          {...qaAttr({ id: `search-users-selected-${sub.userId}` })}
          style={{
            position: 'relative',
            flexShrink: 0,
          }}
        >
          <CachedImage
            src={UserUtils.generateUserProfileImageUrl(sub.userId)}
            alt={sub.name}
            style={{
              width: responsive.avatar.small,
              height: responsive.avatar.small,
              borderRadius: responsive.borderRadius.round,
              objectFit: 'cover',
            }}
          />
          <button
            onClick={() => onRemove(sub)}
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: responsive.button.size.xxs,
              height: responsive.button.size.xxs,
              borderRadius: responsive.borderRadius.round,
              backgroundColor: theme.danger,
              border: `${responsive.border.medium}px solid ${theme.surface}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.white,
              fontSize: responsive.font.base,
              fontWeight: responsive.fontWeight.bold,
              padding: 0,
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};
